import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Report, ReportType, SalesDetails, MaintenanceDetails, ProjectDetails, ProjectUpdate, ProjectWorkflowStatus } from '../../types';
import { Button } from '../../components/ui/Button';
import { BarChart2, Printer, Download, CheckCircle, Circle, User, Phone, MapPin, FileText, MessageSquare, Briefcase, Users2, AlertTriangle, PlusCircle, Paperclip, Send, X, ArrowRight, Calendar, Clock, ChevronLeft } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import useAppStore from '../../store/useAppStore';
import { useAppContext } from '../../hooks/useAppContext';
import { Role } from '../../types';
import { FileUploadModal } from '../../components/common/FileUploadModal';
import { Textarea } from '../../components/ui/Textarea';

interface ReportDetailScreenProps {
    report: Report;
}

const PROJECT_STAGES = [
    { id: 'contract', name: 'العقد' },
    { id: 'firstPayment', name: 'الدفعة الأولى' },
    { id: 'notifyTeam', name: 'إشعار الفريق' },
    { id: 'concreteWorks', name: 'الأعمال الخرسانية' },
    { id: 'secondPayment', name: 'الدفعة الثانية' },
    { id: 'installationComplete', name: 'التركيب' },
    { id: 'deliveryHandover', name: 'التسليم' },
];

const getStatusBadge = (status?: string) => {
    switch (status) {
        case ProjectWorkflowStatus.PendingTeamAcceptance:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">بانتظار موافقة الفريق</span>;
        case ProjectWorkflowStatus.InProgress:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500 border border-blue-500/20">قيد التنفيذ</span>;
        case ProjectWorkflowStatus.FinishingWorks:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">أعمال التشطيبات</span>;
        case ProjectWorkflowStatus.ConcreteWorksDone:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-500 border border-orange-500/20">بانتظار الدفعة الثانية</span>;
        case ProjectWorkflowStatus.TechnicallyCompleted:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">مكتمل فنياً</span>;
        case ProjectWorkflowStatus.Finalized:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">مكتمل</span>;
        default:
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-500 border border-slate-500/20">{status || 'مسودة'}</span>;
    }
}

const InfoItem: React.FC<{ label: string, value?: string | number, isFullWidth?: boolean, icon?: React.ElementType, className?: string }> = ({ label, value, isFullWidth, icon: Icon, className }) => (
    <div className={`bg-slate-800/30 p-3 rounded-xl border border-slate-700/30 ${isFullWidth ? 'col-span-full' : ''} ${className}`}>
        <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-1">
            {Icon && <Icon size={12} />}
            {label}
        </p>
        <p className="font-semibold text-slate-200 truncate" dir={label.includes('هاتف') ? 'ltr' : 'auto'}>{value || '---'}</p>
    </div>
);

const ProjectUpdateItem: React.FC<{ update: ProjectUpdate, canViewAllFiles: boolean, currentUserId?: string }> = ({ update, canViewAllFiles, currentUserId }) => {
    const { t } = useAppContext();
    const isHandoverStage = update.id === 'deliveryHandover' || update.id === 'deliveryHandover_signed';
    const visibleFiles = (canViewAllFiles || isHandoverStage)
        ? update.files
        : update.files?.filter(file => file.uploadedBy === currentUserId);

    return (
        <div className="flex flex-col p-4 bg-slate-800/30 border border-slate-700/30 rounded-xl hover:bg-slate-800/50 transition-colors">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {update.completed ? <CheckCircle size={20} className="text-emerald-500" /> : <Circle size={20} className="text-slate-600" />}
                    <div>
                        <span className={`font-semibold ${update.completed ? 'text-slate-200' : 'text-slate-500'}`}>{update.label}</span>
                        {update.timestamp && <p className="text-xs text-slate-500 mt-0.5">{new Date(update.timestamp).toLocaleString('ar-SA')}</p>}
                    </div>
                </div>
            </div>
            {update.comment && (
                <div className="mt-3 p-3 text-sm bg-slate-900/50 rounded-lg border border-slate-700/30 flex items-start gap-2 text-slate-300">
                    <MessageSquare size={14} className="mt-1 flex-shrink-0 text-slate-500" />
                    <span>{update.comment}</span>
                </div>
            )}
            {visibleFiles && visibleFiles.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700/30 space-y-2">
                    <h5 className="text-xs font-semibold text-slate-500">{t('attachedFiles')}</h5>
                    <div className="flex flex-wrap gap-2">
                        {visibleFiles.map((file) => (
                            <a key={file.id} href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1.5 bg-slate-700/50 rounded-lg border border-slate-600/30 flex items-center gap-1.5 text-slate-300 hover:text-blue-400 hover:border-blue-500/30 transition-all">
                                <Download size={12} /> {file.fileName}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
};

const AdminNotesSection: React.FC<{ report: Report }> = ({ report }) => {
    const { user } = useAppContext();
    const { addAdminNote, addAdminNoteReply } = useAppStore();
    const [newNote, setNewNote] = useState('');
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyContent, setReplyContent] = useState('');

    const handleAddNote = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        addAdminNote(report.id, newNote, user);
        setNewNote('');
    };

    const handleAddReply = (noteId: string) => {
        if (!replyContent.trim()) return;
        addAdminNoteReply(report.id, noteId, replyContent, user);
        setReplyContent('');
        setReplyingTo(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, callback: () => void) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            callback();
        }
    };

    const notes = Array.isArray(report.adminNotes) ? report.adminNotes : [];
    const canInteract = !!user;

    return (
        <div className="glass-panel p-6 rounded-2xl space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="text-blue-400" size={24} />
                <h3 className="text-xl font-bold text-white">الملاحظات والمناقشات</h3>
            </div>

            <div className="space-y-4 max-h-[500px] overflow-y-auto p-4 bg-slate-900/30 rounded-xl border border-slate-700/30 custom-scrollbar">
                {notes.length > 0 ? (
                    notes.map(note => {
                        const isCurrentUserAuthor = user?.id === note.authorId;
                        return (
                            <div key={note.id} className={`flex flex-col ${isCurrentUserAuthor ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-xl p-4 rounded-2xl ${isCurrentUserAuthor ? 'bg-blue-600 text-white rounded-br-none' : 'bg-slate-800 text-slate-200 rounded-bl-none'}`}>
                                    <div className="flex items-center gap-2 mb-1 opacity-80">
                                        <p className="text-xs font-semibold">{note.authorName}</p>
                                        <p className="text-[10px]">{new Date(note.timestamp).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</p>
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{note.content}</p>
                                </div>

                                {/* Replies */}
                                <div className={`w-11/12 mt-2 space-y-2 ${isCurrentUserAuthor ? 'self-end' : 'self-start'}`}>
                                    {note.replies.map(reply => {
                                        const isCurrentUserReplyAuthor = user?.id === reply.authorId;
                                        return (
                                            <div key={reply.id} className={`flex flex-col ${isCurrentUserReplyAuthor ? 'items-end' : 'items-start'}`}>
                                                <div className={`max-w-full p-3 rounded-xl ${isCurrentUserReplyAuthor ? 'bg-blue-500/80 text-white rounded-br-none' : 'bg-slate-700/80 text-slate-200 rounded-bl-none'}`}>
                                                    <div className="flex items-center gap-2 mb-1 opacity-80">
                                                        <p className="text-xs font-semibold">{reply.authorName}</p>
                                                        <p className="text-[10px]">{new Date(reply.timestamp).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</p>
                                                    </div>
                                                    <p className="text-sm">{reply.content}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {canInteract && (
                                        replyingTo === note.id ? (
                                            <div className="flex items-center gap-2 pt-2 animate-fadeIn">
                                                <Textarea
                                                    placeholder="اكتب ردك..."
                                                    value={replyContent}
                                                    onChange={(e) => setReplyContent(e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(e, () => handleAddReply(note.id))}
                                                    rows={1}
                                                    className="flex-1 text-sm bg-slate-800 border-slate-600 focus:border-blue-500"
                                                />
                                                <button className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors" onClick={() => handleAddReply(note.id)}><Send size={16} /></button>
                                                <button className="p-2 rounded-lg bg-slate-700 text-slate-400 hover:bg-slate-600 transition-colors" onClick={() => setReplyingTo(null)}><X size={16} /></button>
                                            </div>
                                        ) : (
                                            <button className="text-xs text-slate-500 hover:text-blue-400 transition-colors px-2 py-1" onClick={() => setReplyingTo(note.id)}>رد على الملاحظة</button>
                                        )
                                    )}
                                </div>
                            </div>
                        )
                    })
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                        <MessageSquare size={32} className="mb-2 opacity-50" />
                        <p className="text-sm">لا توجد ملاحظات. ابدأ المحادثة!</p>
                    </div>
                )}
            </div>
            {canInteract ? (
                <form onSubmit={handleAddNote} className="flex items-center gap-3">
                    <Textarea
                        placeholder="اكتب ملاحظة جديدة..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, () => handleAddNote(e))}
                        rows={1}
                        className="flex-1 bg-slate-900/50 border-slate-700 focus:border-blue-500 text-slate-200 placeholder-slate-500"
                    />
                    <button type="submit" className="p-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 transition-all">
                        <Send size={20} />
                    </button>
                </form>
            ) : (
                <p className="text-xs text-slate-500 text-center">سجّل الدخول لإضافة ملاحظة أو الرد.</p>
            )}
        </div>
    );
};

const ModernReportDetailScreen: React.FC<ReportDetailScreenProps> = ({ report }) => {
    const { printReport, addProjectException, markNotesAsRead } = useAppStore();
    const { t, user, lang } = useAppContext();
    const [isExceptionsModalOpen, setExceptionsModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user && report.id) {
            markNotesAsRead(report.id, user.id);
        }
    }, [report.id, user, markNotesAsRead]);

    const backPath = user?.role === Role.Admin ? '/admin-projects' : '/projects';

    const handleAddException = (files: File[], comment: string) => {
        if (!user) return;
        addProjectException(report.id, files, comment, user.employeeId);
    };

    const projectDetails = report.details as ProjectDetails | undefined;
    const canViewAllFiles = user?.role !== Role.TeamLead;
    const currentUserId = user?.id;

    // Calculate progress for projects
    let progressPercentage = 0;
    if (report.type === ReportType.Project && projectDetails) {
        const reversedUpdates = [...(projectDetails.updates || [])].reverse();
        const lastCompletedUpdate = reversedUpdates.find(u => u.completed);
        const currentStageIndex = lastCompletedUpdate
            ? PROJECT_STAGES.findIndex(s => s.id === lastCompletedUpdate.id)
            : -1;
        progressPercentage = currentStageIndex >= 0
            ? Math.round(((currentStageIndex + 1) / PROJECT_STAGES.length) * 100)
            : 0;
    }

    return (
        <div className="space-y-8 animate-contentFadeIn pb-10">
            {/* Header */}
            <div className="flex flex-col gap-6">
                <button
                    onClick={() => navigate(-1)}
                    className="self-start flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-all group"
                >
                    <ArrowRight size={18} className="group-hover:-translate-x-1 transition-transform" />
                    <span>رجوع</span>
                </button>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-3xl font-bold text-white">تفاصيل التقرير</h1>
                            <span className="px-3 py-1 rounded-lg bg-slate-800 text-slate-400 font-mono text-sm border border-slate-700">{report.id}</span>
                        </div>
                        <div className="flex items-center gap-4 text-slate-400 text-sm">
                            <span className="flex items-center gap-1.5"><User size={14} /> {report.employeeName}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                            <span className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(report.date).toLocaleDateString('ar-SA')}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {getStatusBadge(report.status)}
                        <button
                            onClick={() => printReport(report.id)}
                            className="p-3 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-all border border-slate-700"
                            title={t('print')}
                        >
                            <Printer size={20} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">

                    {/* Project Progress (Only for Projects) */}
                    {report.type === ReportType.Project && (
                        <div className="glass-panel p-6 rounded-2xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500"></div>
                            <div className="flex justify-between items-end mb-4">
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-1">تقدم المشروع</h3>
                                    <p className="text-slate-400 text-sm">نسبة الإنجاز الحالية بناءً على المراحل المكتملة</p>
                                </div>
                                <div className="text-3xl font-bold text-blue-400 font-mono">{progressPercentage}%</div>
                            </div>
                            <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-blue-600 to-purple-600 h-full rounded-full transition-all duration-1000 ease-out relative"
                                    style={{ width: `${progressPercentage}%` }}
                                >
                                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Report Details */}
                    <div className="glass-panel p-6 rounded-2xl">
                        <h3 className="text-xl font-bold text-white mb-6 border-b border-slate-700/50 pb-4">تفاصيل {t(report.type)}</h3>

                        {report.type === ReportType.Sales && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <InfoItem label={t('totalCustomers')} value={(report.details as SalesDetails).totalCustomers} />
                                    <InfoItem label={t('serviceType')} value={(report.details as SalesDetails).serviceType} />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-300 mb-4">{t('customerDetails')}</h4>
                                    <div className="space-y-4">
                                        {(report.details as SalesDetails).customers.map((c) => (
                                            <div key={c.id} className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                    <InfoItem icon={User} label={t('name')} value={c.name} />
                                                    <InfoItem icon={Phone} label={t('phone')} value={c.phone} />
                                                    <InfoItem icon={MapPin} label={t('region')} value={c.region} />
                                                    <InfoItem icon={FileText} label={t('requestType')} value={c.requestType} />
                                                </div>
                                                {c.notes && <div className="p-3 bg-slate-900/30 rounded-lg text-sm text-slate-300 border border-slate-700/30">{c.notes}</div>}
                                                {c.files && c.files.length > 0 && (
                                                    <div className="mt-3 flex flex-wrap gap-2">
                                                        {c.files.map((file, idx) => (
                                                            <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 bg-slate-700/50 rounded flex items-center gap-1.5 text-slate-300 hover:text-blue-400 transition-colors">
                                                                <Download size={12} /> {file.fileName}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {report.type === ReportType.Maintenance && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <InfoItem label={t('customerName')} value={(report.details as MaintenanceDetails).customerName} />
                                    <InfoItem label={t('serviceType')} value={(report.details as MaintenanceDetails).serviceType} />
                                    <InfoItem label={t('workStatus')} value={(report.details as MaintenanceDetails).workStatus} />
                                    <InfoItem label={t('durationHours')} value={(report.details as MaintenanceDetails).duration} />
                                    <InfoItem label={t('location')} value={(report.details as MaintenanceDetails).location} isFullWidth />
                                    <InfoItem label={t('equipmentUsed')} value={(report.details as MaintenanceDetails).equipment} isFullWidth />
                                    <InfoItem label={t('technicalNotes')} value={(report.details as MaintenanceDetails).notes} isFullWidth />
                                </div>
                                {/* Images */}
                                {['beforeImages', 'afterImages'].map((key) => {
                                    const images = (report.details as any)[key];
                                    if (!images || images.length === 0) return null;
                                    return (
                                        <div key={key}>
                                            <h4 className="font-semibold text-slate-300 mb-3">{key === 'beforeImages' ? t('beforeWorkPhotos') : t('afterWorkPhotos')}</h4>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                {images.map((img: any, idx: number) => (
                                                    <a key={idx} href={img.url} target="_blank" rel="noopener noreferrer" className="block group overflow-hidden rounded-xl border border-slate-700/50">
                                                        <img src={img.url} alt={`${key} ${idx}`} className="w-full h-24 object-cover group-hover:scale-110 transition-transform duration-500" />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {report.type === ReportType.Project && projectDetails && (
                            <div className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <h4 className="font-semibold text-blue-400 flex items-center gap-2"><Briefcase size={18} /> {t('basicInformation')}</h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <InfoItem icon={User} label={t('projectOwner')} value={projectDetails.projectOwner} />
                                            <InfoItem icon={Phone} label={t('projectOwnerPhone')} value={projectDetails.projectOwnerPhone} />
                                            <InfoItem icon={MapPin} label={t('projectLocation')} value={projectDetails.location} />
                                            <InfoItem icon={Briefcase} label={t('projectSize')} value={projectDetails.size} />
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <h4 className="font-semibold text-purple-400 flex items-center gap-2"><Users2 size={18} /> {t('technicalTeam')}</h4>
                                        <div className="grid grid-cols-1 gap-3">
                                            <InfoItem icon={User} label={t('teamLeader')} value={useAppStore.getState().technicalTeams.find(t => t.id === report.assignedTeamId)?.leaderName} />
                                            <InfoItem icon={Users2} label={t('teamMembers')} value={useAppStore.getState().technicalTeams.find(t => t.id === report.assignedTeamId)?.members?.join(', ')} />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-emerald-400 mb-4 flex items-center gap-2"><CheckCircle size={18} /> {t('workflowStages')}</h4>
                                    <div className="space-y-3">
                                        {(projectDetails.updates || [])
                                            .filter(u => user?.role !== Role.TeamLead || !['contract', 'firstPayment', 'secondPayment'].includes(u.id))
                                            .map((update) => (
                                                <ProjectUpdateItem key={update.id} update={update} canViewAllFiles={canViewAllFiles} currentUserId={currentUserId} />
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar Content */}
                <div className="space-y-6">
                    {/* Admin Notes */}
                    <AdminNotesSection report={report} />

                    {/* Exceptions (Project Only) */}
                    {report.type === ReportType.Project && (
                        <div className="glass-panel p-6 rounded-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="text-amber-500" size={20} />
                                    <h3 className="text-lg font-bold text-white">{t('exceptions')}</h3>
                                </div>
                                <button
                                    onClick={() => setExceptionsModalOpen(true)}
                                    className="p-2 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500 hover:text-white transition-all"
                                >
                                    <PlusCircle size={18} />
                                </button>
                            </div>

                            <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar">
                                {projectDetails?.exceptions && projectDetails.exceptions.length > 0 ? (
                                    projectDetails.exceptions.map(exc => (
                                        <div key={exc.id} className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl">
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="text-[10px] text-amber-500/70">{new Date(exc.timestamp).toLocaleString()}</span>
                                            </div>
                                            <p className="text-sm text-slate-300 mb-2">{exc.comment}</p>
                                            {exc.files && exc.files.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {exc.files.map(file => (
                                                        <a key={file.id} href={file.url} target="_blank" rel="noreferrer" className="text-[10px] flex items-center gap-1 bg-amber-500/10 text-amber-400 px-2 py-1 rounded hover:bg-amber-500/20 transition-colors">
                                                            <Paperclip size={10} /> {file.fileName}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-center text-slate-500 py-4">{t('noExceptionsRecorded')}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Modifications Log */}
                    {report.modifications && report.modifications.length > 0 && (
                        <div className="glass-panel p-6 rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-4">سجل التعديلات</h3>
                            <ul className="space-y-3">
                                {report.modifications.map((mod, index) => (
                                    <li key={index} className="text-xs text-slate-400 flex items-start gap-2">
                                        <Clock size={14} className="mt-0.5 flex-shrink-0" />
                                        <span>
                                            تم التعديل بواسطة <span className="font-semibold text-slate-200">{mod.modifiedBy}</span>
                                            <br />
                                            <span className="opacity-70">{new Date(mod.timestamp).toLocaleString('ar-SA')}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {isExceptionsModalOpen && (
                <FileUploadModal
                    isOpen={isExceptionsModalOpen}
                    onClose={() => setExceptionsModalOpen(false)}
                    onSubmit={handleAddException}
                    title={t('addNewException')}
                    submitButtonText={t('add')}
                    required
                />
            )}
        </div>
    );
};

export default ModernReportDetailScreen;
